import {
  DataTexture,
  DoubleSide,
  MeshStandardMaterial,
  RepeatWrapping,
  SRGBColorSpace,
  TextureLoader,
} from 'three';

export interface MaterialSet {
  ramp: MeshStandardMaterial;
  ground: MeshStandardMaterial;
  platform: MeshStandardMaterial;
  /** Pillars/skirts — distinct dark trim so structure reads apart. */
  decor: MeshStandardMaterial;
}

/**
 * Procedural grain texture — a tileable, mottled grayscale generated as raw
 * pixel data (no asset files, no DOM, works in tests and the browser alike).
 * Used as a subtle diffuse + roughness map so surfaces read as matte concrete
 * rather than dead flat, without the blocky tiling the photo PBR skin had.
 *
 * Tileable: the value-noise lattice wraps on its period, so repeated copies meet
 * seamlessly. Values sit in a narrow bright band (~0.7–1.0) so the map only
 * gently modulates the base color.
 */
function makeGrainTexture(size = 256): DataTexture {
  const data = new Uint8Array(size * size * 4);

  // Deterministic per-lattice-cell value in [0,1), wrapped to `period` for tiling.
  const cell = (ix: number, iy: number, period: number): number => {
    const x = ((ix % period) + period) % period;
    const y = ((iy % period) + period) % period;
    let h = (x * 374761393 + y * 668265263) | 0;
    h = (h ^ (h >>> 13)) * 1274126177;
    h = h ^ (h >>> 16);
    return (h >>> 0) / 4294967295;
  };
  const smooth = (t: number): number => t * t * (3 - 2 * t);
  const noise = (fx: number, fy: number, period: number): number => {
    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const tx = smooth(fx - x0);
    const ty = smooth(fy - y0);
    const v00 = cell(x0, y0, period);
    const v10 = cell(x0 + 1, y0, period);
    const v01 = cell(x0, y0 + 1, period);
    const v11 = cell(x0 + 1, y0 + 1, period);
    const a = v00 + (v10 - v00) * tx;
    const b = v01 + (v11 - v01) * tx;
    return a + (b - a) * ty;
  };

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      // Two octaves (periods 8 + 32, both wrap → seamless) for soft mottling.
      const n = noise(u * 8, v * 8, 8) * 0.6 + noise(u * 32, v * 32, 32) * 0.4;
      const g = Math.round((0.7 + n * 0.3) * 255);
      const i = (y * size + x) * 4;
      data[i] = g;
      data[i + 1] = g;
      data[i + 2] = g;
      data[i + 3] = 255;
    }
  }

  const tex = new DataTexture(data, size, size);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
}

/** Dark trim for pillars/skirts (catches sun + sky highlights). */
function decorMaterial(): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color: 0x3a4048,
    roughness: 0.6,
    metalness: 0.55,
    envMapIntensity: 0.6,
  });
}

/**
 * Level materials: flat matte base colors (the clean look the owner prefers over
 * the photo PBR skin) lifted by a shared procedural grain map so the ramps and
 * floors carry some surface texture. Geometry already has world-scale UVs from
 * generateBoxUVs, so the grain tiles at a consistent real-world scale.
 */
function levelMaterials(): MaterialSet {
  const grain = makeGrainTexture();
  // A separate diffuse instance so its colorSpace doesn't affect the linear
  // roughness sampling; same pixel data, shared via clone (cheap, no regen).
  const diffuse = grain.clone();
  diffuse.colorSpace = SRGBColorSpace;
  diffuse.needsUpdate = true;

  const surfaced = (color: number, roughness: number): MeshStandardMaterial =>
    new MeshStandardMaterial({
      color,
      roughness,
      metalness: 0.05,
      map: diffuse,
      roughnessMap: grain,
      side: DoubleSide, // ride faces read solid from any angle (capless lanes included)
    });

  return {
    ramp: surfaced(0x9398a0, 0.85),
    ground: surfaced(0x66745f, 1.0),
    platform: surfaced(0x767f8a, 0.9),
    decor: decorMaterial(),
  };
}

let cached: Promise<MaterialSet> | null = null;

/** Shared level materials. Cached so every map reuses the same instances. */
export function loadMaterials(): Promise<MaterialSet> {
  cached ??= Promise.resolve(levelMaterials());
  return cached;
}

/** Background-load a diffuse (+ optional normal) into an existing material, in
 * the browser only — failures (tests / missing asset) are swallowed so the
 * material keeps its base color. Never blocks map building. */
function enhanceWithTexture(
  mat: MeshStandardMaterial,
  diffuseUrl: string,
  normalUrl?: string,
): void {
  try {
    const loader = new TextureLoader();
    loader
      .loadAsync(diffuseUrl)
      .then((t) => {
        t.wrapS = RepeatWrapping;
        t.wrapT = RepeatWrapping;
        t.colorSpace = SRGBColorSpace;
        mat.map = t;
        mat.needsUpdate = true;
      })
      .catch(() => {});
    if (normalUrl) {
      loader
        .loadAsync(normalUrl)
        .then((t) => {
          t.wrapS = RepeatWrapping;
          t.wrapT = RepeatWrapping;
          mat.normalMap = t;
          mat.needsUpdate = true;
        })
        .catch(() => {});
    }
  } catch {
    // No TextureLoader environment (tests) — keep the flat base color.
  }
}

/**
 * Arcane Academy skin: glowing frosted-glass/crystal ramps + crystal platforms,
 * a marble base, and rune-etched glowing trim. Emissive so it reads magical even
 * at low quality (bloom at high/ultra adds the halo). Shares the grain map; the
 * marble diffuse loads in the background (browser only).
 */
function arcaneMaterials(): MaterialSet {
  const grain = makeGrainTexture();
  const diffuse = grain.clone();
  diffuse.colorSpace = SRGBColorSpace;
  diffuse.needsUpdate = true;

  const glass = (color: number, emissive: number, intensity: number, roughness: number) =>
    new MeshStandardMaterial({
      color,
      emissive,
      emissiveIntensity: intensity,
      roughness,
      metalness: 0.12,
      map: diffuse,
      roughnessMap: grain,
      side: DoubleSide, // ride faces read solid from any angle (capless lanes included)
    });

  // Bright icy cyan-blue crystal: high value + a brighter emissive so the ride
  // faces clearly stand out against the dark indigo void (readability, pillar 3) —
  // a muted blue used to blend into the night sky.
  const ramp = glass(0x9cc8ee, 0x3aa0ff, 0.6, 0.32); // crystal-glass ride faces
  const platform = glass(0xb6d6f2, 0x55a8ff, 0.45, 0.3); // brighter crystal pads
  const ground = new MeshStandardMaterial({
    color: 0x9a8fc0,
    roughness: 0.72,
    metalness: 0.06,
    side: DoubleSide,
  });
  const decor = new MeshStandardMaterial({
    color: 0x9a8bff,
    emissive: 0x5a3cf0,
    emissiveIntensity: 0.55,
    roughness: 0.4,
    metalness: 0.45,
  });

  enhanceWithTexture(
    ground,
    'assets/textures/marble_01/marble_01_Diffuse_1k.jpg',
    'assets/textures/marble_01/marble_01_nor_gl_1k.jpg',
  );

  return { ramp, ground, platform, decor };
}

let arcaneCached: Promise<MaterialSet> | null = null;

/** Glowing crystal/glass materials for the Arcane Academy. Cached + shared. */
export function loadArcaneMaterials(): Promise<MaterialSet> {
  arcaneCached ??= Promise.resolve(arcaneMaterials());
  return arcaneCached;
}

/**
 * The Fall Line skin: packed-snow ride faces and ice-blue platforms over dark
 * alpine rock. Daylight blizzard — no emissive (bloom isn't forced), just a low
 * roughness so the ice catches the cool sun. Bright values keep the ramp faces
 * tonally distinct from the white-out backdrop (readability, GDD pillar 3).
 * Shares the procedural grain map, so it's asset-free and works in tests.
 */
function snowMaterials(): MaterialSet {
  const grain = makeGrainTexture();
  const diffuse = grain.clone();
  diffuse.colorSpace = SRGBColorSpace;
  diffuse.needsUpdate = true;

  const iced = (color: number, roughness: number): MeshStandardMaterial =>
    new MeshStandardMaterial({
      color,
      roughness,
      metalness: 0.04,
      map: diffuse,
      roughnessMap: grain,
      side: DoubleSide, // ride faces read solid from any angle (capless lanes included)
    });

  return {
    ramp: iced(0xc7d8ec, 0.42), // packed-snow/ice ride faces — bright, slightly glossy
    platform: iced(0xe7f0fb, 0.5), // brighter packed-snow pads
    ground: iced(0x5d6675, 0.95), // dark alpine rock far below
    decor: new MeshStandardMaterial({ color: 0x4a525e, roughness: 0.7, metalness: 0.2 }),
  };
}

let snowCached: Promise<MaterialSet> | null = null;

/** Packed-snow / ice materials for The Fall Line. Cached + shared. */
export function loadSnowMaterials(): Promise<MaterialSet> {
  snowCached ??= Promise.resolve(snowMaterials());
  return snowCached;
}
